import React, { memo } from 'react';
import { getPdfLabelStyle, getPdfTitleStyle, pdfStyles } from '../../pdfStyles';
import { Image, Page, Text, View } from '@react-pdf/renderer';
import {
  ActionsTable,
  EmptyChart,
  Footer,
  FooterStartComponent,
  Header,
  HeaderEndComponent,
  LegendExceedance,
  LegendTolerance,
  NoData,
  PdfLinkifyText,
  ScenariosTable,
  TitleLink,
} from '../../comps';
import { isEqual } from 'lodash';
import { AssessmentPdfQuery } from 'src/graphql/_generated';
import { Stats } from './Stats';
import { ColorSet } from 'src/redux/reducers/theme';
import { formatDate } from 'src/utils/misc';
import { ActionsConf } from '../../types';

type AssessmentGql = NonNullable<AssessmentPdfQuery['assessment']>;

interface AssessmentSummaryProps {
  assessment: AssessmentGql;
  scenarios: AssessmentGql['scenarios'];
  onDone: (pageNumber: number) => void;
  chart: string;
  includesRMP: boolean;
  includesRiskTolerance: boolean;
  colors: ColorSet;
  showAllProjected: boolean;
  includesActions: ActionsConf;
  logoSrc?: string;
  secondaryColor?: string | null;
}

export const AssessmentSummary: React.FC<AssessmentSummaryProps> = memo(
  ({
    assessment,
    scenarios,
    onDone,
    chart,
    includesRMP,
    includesRiskTolerance,
    colors,
    showAllProjected,
    includesActions,
    logoSrc,
    secondaryColor,
  }) => {
    const isEmptyChart = assessment.AssessmentLiveRisk?.chart?.every((el) => el.y === 1 && el.x === 0) ?? true;
    const titleStyle = getPdfTitleStyle(secondaryColor);
    const labelStyle = getPdfLabelStyle(secondaryColor);

    return (
      <Page size={'A4'} wrap style={pdfStyles.page}>
        <Header endComponent={<HeaderEndComponent title="BACK TO TOP" />} colors={colors} logoSrc={logoSrc} />
        <View
          // @ts-ignore
          render={(data) => {
            onDone(data.pageNumber);
          }}
        />
        <View style={pdfStyles.section}>
          <TitleLink
            title={assessment.name}
            intLinkId={assessment.name.toLowerCase()}
            extLink={`risk-assessments/${assessment.id}`}
            colors={colors}
          />

          <View style={pdfStyles.spacer8} />
          <View style={pdfStyles.spacer4} />

          <View style={{ flexDirection: 'row' }}>
            <View style={{ width: 110 }}>
              <Text style={labelStyle}>REVIEW DATE</Text>
              <View style={pdfStyles.spacer1} />
              <Text style={pdfStyles.value}>{formatDate(assessment.reviewDate, { emptyValue: '-' })}</Text>
            </View>
            <View style={{ width: 120 }}>
              <Text style={labelStyle}>EDITED</Text>
              <View style={pdfStyles.spacer1} />
              <Text style={pdfStyles.value}>
                {formatDate(assessment.editedAt, { emptyValue: '-', formatType: 'datetime' })}
              </Text>
            </View>
            <View>
              <Text style={labelStyle}>UPDATED</Text>
              <View style={pdfStyles.spacer1} />
              <Text style={pdfStyles.value}>
                {formatDate(assessment.meta?.updatedAt, { emptyValue: '-', formatType: 'datetime' })}
              </Text>
            </View>
          </View>

          <View style={pdfStyles.spacer16} />
          <View style={pdfStyles.spacer8} />

          <Stats
            assessment={assessment}
            includesRMP={includesRMP}
            colors={colors}
            showAllProjected={showAllProjected}
          />

          <View style={pdfStyles.spacer16} />
          <View style={pdfStyles.spacer8} />

          <Text style={titleStyle}>LOSS EXCEEDANCE CURVE</Text>
          <View style={pdfStyles.spacer4} />
          {isEmptyChart ? (
            <EmptyChart colors={colors} />
          ) : (
            <>
              <Image src={chart} style={pdfStyles.chartImage} />
              <View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 12, marginTop: -8 }}>
                <LegendExceedance colors={colors} showAllProjected={showAllProjected} />
                {includesRiskTolerance && <LegendTolerance colors={colors} />}
              </View>
            </>
          )}

          <View style={pdfStyles.spacer8} />

          <Text style={labelStyle}>SCOPE</Text>
          <View style={pdfStyles.spacer1} />
          {assessment.description ? (
            <PdfLinkifyText>{assessment.description}</PdfLinkifyText>
          ) : (
            <NoData colors={colors} />
          )}

          <ActionsTable
            data={assessment.actions}
            colors={colors}
            includesActions={includesActions}
            secondaryColor={secondaryColor}
          />
          <View style={pdfStyles.spacer16} />
          <ScenariosTable data={scenarios || []} colors={colors} secondaryColor={secondaryColor} />
        </View>

        <Footer footerComponent={<FooterStartComponent title={assessment.name} />} colors={colors} />
      </Page>
    );
  },
  (prevProps, nextProps) => isEqual(prevProps, nextProps),
);
